import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  useGetScannerBundleAdminQuery,
  useRecordAttendanceAdminMutation,
} from '@proaxiv/server-api'
import { formatEventDateTime, formatLocalDateTime } from '../utils/datetime'
import { buildScannerMap, toParticipantRole, verifyScannerQr } from '../utils/ticketVerification'
import { formatFullName } from '../utils/participantMappers'
import { formatEmail, toHalfWidth } from '../utils/email'
import type { ScannerBundle, ScannerParticipantEntry } from '../utils/ticketVerification'
import { playApproved, playRejected } from '../utils/feedbackSound'
import { useQrScanner } from '../hooks/useQrScanner'
import { useScanLog, type ScanLogEntry, type ScanSyncStatus } from '../hooks/useScanLog'
import { RoleBadge } from '../components/RoleBadge'
import { Button } from '../components/Button'

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_RESET_MS = 3_000

// The kiosk session can stay open for hours while staff keep scanning, but the scanner
// bundle is only a snapshot taken when the query first fires. An organiser can accept a
// registration (or close/re-open registration) at any point during that window — without
// a periodic refetch, a participant accepted after the kiosk opened stays unknown to
// `verifyScannerQr` and is rejected forever, even though the backend already allows their
// check-in (issue #246: "Registration Just Before the Deadline"). Polling keeps the bundle
// eventually-consistent with the backend without requiring staff to reload the page.
const SCANNER_BUNDLE_POLL_MS = 15_000

// // TEMPORARY (frontend-only mock): the backend crypto-verification bundle isn't
// // wired up for on-site testing yet. While this flag is on, ANY decoded QR code
// // is treated as an approved participant using fixed mock data, skipping the
// // real signature checks and the attendance-recording API call.
// // TODO: remove this flag (and MOCK_PARTICIPANT_ENTRY) once real QR verification
// // is validated end-to-end.
// const MOCK_APPROVE_ANY_QR = false

// const MOCK_PARTICIPANT_ENTRY: ScannerParticipantEntry = {
//   id: 'mock-participant',
//   firstName: 'Taro',
//   lastName: 'Yamada',
//   email: 'taro.yamada@example.com',
//   status: 'ACCEPTED',
//   role: 'AUDIENCE',
//   secretToken: 'mock',
//   rawCiphertext: '',
// }

// ── Phases & types ────────────────────────────────────────────────────────────

type Phase = 'loading' | 'ready' | 'scanning'
type ScanStatus = 'none' | 'approved' | 'rejected' | 'already_attended'

// ── Design tokens (fixed — independent of event branding) ────────────────────
//
// The check-in kiosk intentionally uses its own design language so staff get a
// consistent, high-contrast interface regardless of which event they work.

// ── Icons ─────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-20 w-20"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-20 w-20"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const ScanIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-14 w-14"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="5" y="5" width="3" height="3" />
    <rect x="16" y="5" width="3" height="3" />
    <rect x="5" y="16" width="3" height="3" />
    <path d="M14 14h3v3M17 17h3v3M14 20h3" />
  </svg>
)

/** Chevron used by the scanned-participants disclosure toggle — rotates to signal
 *  open/closed state, matching the DetailsToggleButton convention used elsewhere in the
 *  app (see #272). */
const ChevronDownIcon = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5 shrink-0 transition-transform duration-150"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const ArrowLeftIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
)

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

// ── Animated scanning corners overlay ─────────────────────────────────────────

function ScanCorners() {
  return (
    <>
      <style>{`
        @keyframes kiosk-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        @keyframes kiosk-scan-line {
          0% { transform: translateY(0); opacity: 0.7; }
          50% { opacity: 1; }
          100% { transform: translateY(160px); opacity: 0.7; }
        }
        .kiosk-corners { animation: kiosk-pulse 2s ease-in-out infinite; }
        .kiosk-scan-line { animation: kiosk-scan-line 2.2s linear infinite; }
      `}</style>
      <div className="kiosk-corners pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="relative h-40 w-40">
          {/* Top-left */}
          <span className="absolute top-0 left-0 h-6 w-6 rounded-tl-sm border-t-3 border-l-3 border-indigo-400" />
          {/* Top-right */}
          <span className="absolute top-0 right-0 h-6 w-6 rounded-tr-sm border-t-3 border-r-3 border-indigo-400" />
          {/* Bottom-left */}
          <span className="absolute bottom-0 left-0 h-6 w-6 rounded-bl-sm border-b-3 border-l-3 border-indigo-400" />
          {/* Bottom-right */}
          <span className="absolute right-0 bottom-0 h-6 w-6 rounded-br-sm border-r-3 border-b-3 border-indigo-400" />
          {/* Scan line */}
          <div className="kiosk-scan-line absolute top-0 right-1 left-1 h-px bg-gradient-to-r from-transparent via-indigo-400 to-transparent" />
        </div>
      </div>
    </>
  )
}

// ── Loading phase ─────────────────────────────────────────────────────────────

function LoadingPhase({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-6 py-24">
      {error ? (
        <>
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10">
            <XIcon />
          </div>
          <p className="max-w-xs text-center text-sm text-[#8B949E]">{error}</p>
          <Button
            variant="unstyled"
            onClick={onRetry}
            className="rounded-xl border border-[#30363D] bg-[#161B22] px-6 py-2.5 text-sm font-medium text-[#E6EDF3] transition hover:border-indigo-500/50 hover:bg-indigo-500/10"
          >
            Retry
          </Button>
        </>
      ) : (
        <>
          <div className="relative flex h-14 w-14 items-center justify-center">
            <div className="absolute inset-0 animate-spin rounded-full border-2 border-[#30363D] border-t-indigo-400" />
          </div>
          <p className="text-sm text-[#8B949E]">{t('verify_loading')}</p>
        </>
      )}
    </div>
  )
}

// ── Ready phase ───────────────────────────────────────────────────────────────

function ReadyPhase({ bundle, onStart }: { bundle: ScannerBundle; onStart: () => void }) {
  const { t, i18n } = useTranslation()
  const startDate = bundle.event.startDate
    ? formatEventDateTime(bundle.event.startDate, bundle.event.timezone, i18n.language)
    : null

  return (
    <div className="flex flex-col items-center gap-10 py-12">
      {/* Event card */}
      <div className="w-full max-w-sm rounded-2xl border border-[#30363D] bg-[#161B22] p-8 text-center shadow-xl">
        {/* Indigo dot accent */}
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-500/15">
          <div className="h-3 w-3 rounded-full bg-indigo-400" />
        </div>
        <h2 className="text-xl font-bold tracking-tight text-[#E6EDF3]">{bundle.event.title}</h2>
        {startDate && (
          <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-[#8B949E]">
            <CalendarIcon />
            <span>{startDate}</span>
          </div>
        )}
      </div>

      {/* Start CTA */}
      <div className="flex flex-col items-center gap-3">
        <p className="text-sm font-medium text-[#8B949E]">{t('verify_ready_title')}</p>
        <Button
          variant="unstyled"
          onClick={onStart}
          className="group relative overflow-hidden rounded-2xl bg-indigo-600 px-10 py-4 text-base font-bold text-white shadow-lg shadow-indigo-500/20 transition hover:bg-indigo-500 active:scale-[0.97]"
          data-testid="start-scanning-btn"
        >
          <span className="relative z-10">{t('verify_start_btn')}</span>
          {/* Shimmer */}
          <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
        </Button>
      </div>
    </div>
  )
}

// ── Result tiles (50/50 row: approve/reject icon | participant details) ──────
//
// Split into two tiles so each can occupy exactly half the row's width on
// tablet — the icon side gives staff an unmissable go/no-go signal at a
// glance, while the participant side surfaces who was just scanned.

const RESULT_STYLE_APPROVED = {
  background: 'linear-gradient(135deg, #0D2818 0%, #0F3320 100%)',
  border: '1px solid #1F6B2E',
}
const RESULT_STYLE_REJECTED = {
  background: 'linear-gradient(135deg, #2D0B0B 0%, #3A0D0D 100%)',
  border: '1px solid #5A1A1A',
}

function ResultIcon({ status }: { status: ScanStatus }) {
  const { t } = useTranslation()

  if (status === 'none') {
    return (
      <div className="flex h-full min-h-[160px] flex-col items-center justify-center gap-3 rounded-2xl border border-[#30363D] bg-[#161B22] p-6 text-center">
        <span className="text-[#30363D]">
          <ScanIcon />
        </span>
        <p className="max-w-[16ch] text-xs leading-relaxed text-[#8B949E]">
          {t('verify_status_none')}
        </p>
      </div>
    )
  }

  const approvedLike = status === 'approved' || status === 'already_attended'
  // The visible label below already distinguishes "Approved" from "Already checked in" —
  // the aria-label must match it exactly (previously hardcoded to "Approved" for both),
  // otherwise screen-reader users are told a duplicate scan was newly approved.
  const statusLabel =
    status === 'already_attended'
      ? t('verify_status_already_attended')
      : approvedLike
        ? t('verify_status_approved')
        : t('verify_status_rejected')

  return (
    <div
      className="flex h-full min-h-[160px] flex-col items-center justify-center gap-2 rounded-2xl p-6 text-center"
      style={approvedLike ? RESULT_STYLE_APPROVED : RESULT_STYLE_REJECTED}
      role="status"
      aria-label={statusLabel}
    >
      <div
        className={`flex h-16 w-16 items-center justify-center rounded-full ${
          approvedLike ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
        }`}
        style={{
          boxShadow: approvedLike
            ? '0 0 32px rgba(52,211,153,0.25)'
            : '0 0 32px rgba(248,81,73,0.2)',
        }}
      >
        {approvedLike ? <CheckIcon /> : <XIcon />}
      </div>
      <p
        className={`text-xl font-extrabold tracking-tight ${
          approvedLike ? 'text-emerald-300' : 'text-red-300'
        }`}
      >
        {statusLabel}
      </p>
    </div>
  )
}

function ParticipantInfoTile({
  status,
  approvedEntry,
}: {
  status: ScanStatus
  approvedEntry: ScannerParticipantEntry | null
}) {
  const { t, i18n } = useTranslation()

  if (status === 'none' || status === 'rejected' || !approvedEntry) {
    return (
      <div className="flex h-full min-h-[160px] items-center justify-center rounded-2xl border border-[#30363D] bg-[#161B22] p-6 text-center">
        <p className="max-w-[20ch] text-xs leading-relaxed text-[#8B949E]">
          {t('verify_participant_placeholder')}
        </p>
      </div>
    )
  }

  const name = formatFullName(approvedEntry.firstName, approvedEntry.lastName)
  const role = toParticipantRole(approvedEntry.role)

  return (
    <div
      className="flex h-full min-h-[160px] min-w-0 flex-col items-center justify-center gap-2 rounded-2xl p-6 text-center"
      style={RESULT_STYLE_APPROVED}
    >
      <p className="w-full max-w-full text-xl font-bold break-words text-[#E6EDF3]">{name}</p>
      <p className="w-full max-w-full text-sm break-all text-[#8B949E]">{approvedEntry.email}</p>
      {role && <RoleBadge role={role} size="lg" className="mt-1" data-testid="scan-role" />}
      {status === 'already_attended' && approvedEntry.attendedAt && (
        <p className="mt-1 text-xs text-emerald-400/70">
          {t('verify_status_already_attended_at', {
            time: formatLocalDateTime(approvedEntry.attendedAt, i18n.language),
          })}
        </p>
      )}
    </div>
  )
}

// ── Sync status pill (scan-log rows) ─────────────────────────────────────────

function SyncStatusPill({ status }: { status: ScanSyncStatus }) {
  const { t } = useTranslation()

  if (status === 'syncing') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold text-amber-300">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" />
        {t('verify_scan_log_status_syncing')}
      </span>
    )
  }

  if (status === 'error') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-500/10 px-2.5 py-1 text-[10px] font-semibold text-red-400">
        <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
        {t('verify_scan_log_status_error')}
      </span>
    )
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold text-emerald-300">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
      {t('verify_scan_log_status_synced')}
    </span>
  )
}

// ── Scanned-participants list ─────────────────────────────────────────────────

function ScanLogList({ entries }: { entries: ScanLogEntry[] }) {
  const { t } = useTranslation()
  // Closed by default (#272): the list surfaces participant names/emails, which
  // shouldn't sit on-screen at a check-in kiosk unless staff deliberately open it. The
  // aggregate summary counts above stay visible either way — they carry no personal info.
  const [open, setOpen] = useState(false)

  const { syncedCount, syncingCount, errorCount } = useMemo(
    () => ({
      syncedCount: entries.filter((e) => e.syncStatus === 'synced').length,
      syncingCount: entries.filter((e) => e.syncStatus === 'syncing').length,
      errorCount: entries.filter((e) => e.syncStatus === 'error').length,
    }),
    [entries]
  )

  return (
    <div className="flex flex-col gap-3">
      {/* Summary */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-[#30363D] bg-[#161B22] px-4 py-3 text-sm">
        <span className="font-semibold text-emerald-300" data-testid="scan-log-synced-count">
          {t('verify_scan_log_synced', { count: syncedCount })}
        </span>
        <span className="font-semibold text-amber-300" data-testid="scan-log-pending-count">
          {t('verify_scan_log_pending', { count: syncingCount })}
        </span>
        <span className="font-semibold text-red-400" data-testid="scan-log-error-count">
          {t('verify_scan_log_errors', { count: errorCount })}
        </span>
      </div>

      {/* Disclosure toggle — participant names/emails only render once staff click this
          open; same label + rotating chevron regardless of state (predictable, matches
          the DetailsToggleButton pattern used in the participant tables). */}
      <Button
        type="button"
        variant="unstyled"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="scan-log-panel"
        data-testid="scan-log-toggle"
        className="flex items-center justify-center gap-1.5 rounded-xl border border-[#30363D] bg-[#161B22] px-4 py-2.5 text-xs font-semibold text-[#8B949E] transition-colors hover:border-indigo-500/50 hover:text-[#E6EDF3]"
      >
        <ChevronDownIcon open={open} />
        {t('verify_scan_log_toggle')}
      </Button>

      {open && (
        <div id="scan-log-panel" data-testid="scan-log-panel">
          {entries.length === 0 ? (
            <p className="rounded-xl border border-[#30363D] bg-[#161B22] px-4 py-6 text-center text-xs text-[#8B949E]">
              {t('verify_scan_log_empty')}
            </p>
          ) : (
            <ul className="flex flex-col gap-2" data-testid="scan-log-list">
              {entries.map((entry) => {
                const role = toParticipantRole(entry.role)
                const name = formatFullName(entry.firstName, entry.lastName)
                return (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[#30363D] bg-[#161B22] px-4 py-2.5"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-semibold text-[#E6EDF3]">{name}</span>
                      {entry.email && (
                        <span className="truncate text-xs text-[#8B949E]">{entry.email}</span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {role && <RoleBadge role={role} />}
                      <SyncStatusPill status={entry.syncStatus} />
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// ── Scanning phase ────────────────────────────────────────────────────────────

type RecordAttendanceFn = (arg: { eventId: string; participantId: string }) => {
  unwrap: () => Promise<unknown>
}

interface ScanningPhaseProps {
  bundle: ScannerBundle
  /** RTK Query `fulfilledTimeStamp` of the latest scanner-bundle fetch. Used so the
   *  confirmed-synced reconcile runs on every successful refetch — even when
   *  structural sharing reuses the previous `participants` array (identical ACCEPTED
   *  payload), which would otherwise skip a `[bundle.participants]`-only effect. */
  bundleFetchedAt: number | undefined
  eventId: string
  recordAttendance: RecordAttendanceFn
}

function ScanningPhase({ bundle, bundleFetchedAt, eventId, recordAttendance }: ScanningPhaseProps) {
  const { t } = useTranslation()

  // Recomputed (not snapshotted once) so a background refetch of the scanner bundle
  // — polled periodically by the parent, see `SCANNER_BUNDLE_POLL_MS` — picks up
  // participants accepted after this scanning session started, without staff having to
  // reload the page (issue #246).
  const scannerMap = useMemo(() => buildScannerMap(bundle.participants), [bundle.participants])

  const [status, setStatus] = useState<ScanStatus>('none')
  const [approvedEntry, setApprovedEntry] = useState<ScannerParticipantEntry | null>(null)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [manualEmail, setManualEmail] = useState('')
  const [manualError, setManualError] = useState<string | null>(null)
  const [manualSuggestionsOpen, setManualSuggestionsOpen] = useState(false)
  const manualInputRef = useRef<HTMLInputElement>(null)

  // Search matches email OR first/last name (either order), so staff can find someone by
  // whatever they have on hand — a printed list often has names, not emails. Email
  // comparisons additionally normalize full-width→half-width (same as every other email
  // entry point in the app — see #175/#271: a JP IME left in full-width mode turns typed
  // ASCII, most visibly "-", into its full-width Unicode form). Name comparisons apply the
  // same half-width normalization (harmless no-op on kana/kanji) but keep internal spaces so
  // "first last" / "last first" queries still match the joined name blob below.
  //
  // Selecting a suggestion still fills the field with the participant's real email (see
  // `selectManualSuggestion`), so the actual check-in match in `handleManualSubmit` continues
  // to require an exact email — this only widens how a match is *found*, not how it is
  // *confirmed*.
  const manualSuggestions = useMemo(() => {
    const nameQuery = toHalfWidth(manualEmail).trim().toLowerCase()
    if (nameQuery.length < 1) return []
    const emailQuery = formatEmail(manualEmail)
    return bundle.participants
      .filter((p) => {
        if (p.status !== 'ACCEPTED' && p.status !== 'ATTENDED') return false
        if (formatEmail(p.email).includes(emailQuery)) return true
        const nameBlob = toHalfWidth(
          `${p.firstName} ${p.lastName} ${p.lastName} ${p.firstName}`
        ).toLowerCase()
        return nameBlob.includes(nameQuery)
      })
      .slice(0, 8)
  }, [bundle.participants, manualEmail])

  const videoRef = useRef<HTMLVideoElement>(null)
  const aliveRef = useRef(true)

  const { entries: scanLog, addEntry, updateStatus } = useScanLog(bundle.event.id)
  // Participant ids whose attendance this tab has *confirmed* synced to the backend
  // (or is mid-flight syncing). Read/written via a ref rather than the `scanLog` state
  // array because `handleDecode` (the camera-scan path) is wrapped in `useCallback`
  // with a dependency array that intentionally excludes `scanLog` (see its comment), so
  // the camera keeps decoding with one stable callback instead of being torn down and
  // restarted on every scan. That stable callback closes over whichever `handleApproved`
  // reference existed when it was created — a plain function re-created every render —
  // and never sees a later render's `scanLog`, so re-scanning the same participant later
  // in the session always compared against a stale, effectively-empty scan log and fell
  // through to "newly approved" (issue #216). A ref sidesteps the closure entirely:
  // `.current` is always the live value no matter which render's closure reads it.
  //
  // Only *synced* (or in-flight) scans are added here — never a scan whose API call
  // failed, and never an entry rehydrated from `sessionStorage` after a refresh. The
  // scan log is a UI convenience, not proof of server state: an organiser can revert a
  // participant to not-attended from Event Management at any time, and this tab never
  // learns about it. Treating a stored/failed scan as "already attended" would then
  // suppress the very `recordAttendance` call needed to check them in again, so a
  // successful-looking re-scan would leave the participant unattended.
  const confirmedSyncedIdsRef = useRef<Set<string>>(new Set())
  const inFlightIdsRef = useRef<Set<string>>(new Set())

  // Reconcile the confirmed-synced set with the server whenever the bundle refreshes
  // (the 15s poll, or the `ScannerBundle` invalidation fired by every attendance
  // mutation). Once the server no longer shows a participant as ATTENDED — e.g. an
  // organiser set them back to not-attended — this tab's earlier confirmation is stale
  // and must not suppress a fresh check-in. In-flight syncs are exempt: their POST may
  // simply not have landed yet when the poll response was fetched.
  useEffect(() => {
    for (const p of bundle.participants) {
      if (p.status !== 'ATTENDED' && !inFlightIdsRef.current.has(p.id)) {
        confirmedSyncedIdsRef.current.delete(p.id)
      }
    }
  }, [bundle.participants, bundleFetchedAt])

  function handleApproved(entry: ScannerParticipantEntry) {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    // Manual check-in passes the live bundle row; the camera path passes the
    // `scannerMap` entry (recomputed on each bundle refetch). Either way, a
    // same-session check-in can leave `status` stale at ACCEPTED until the
    // invalidation refetch lands — `confirmedSyncedIdsRef` covers that lag.
    // After an organiser unmarks them, the reconcile effect above drops the id
    // once a refetch reports anything other than ATTENDED.
    const alreadyAttended =
      entry.status === 'ATTENDED' || confirmedSyncedIdsRef.current.has(entry.id)
    setStatus(alreadyAttended ? 'already_attended' : 'approved')
    setApprovedEntry(entry)
    playApproved()

    // Already-attended scans were already logged the first time they were
    // checked in — don't add a duplicate row to the scanned-participants list.
    if (!alreadyAttended) {
      confirmedSyncedIdsRef.current.add(entry.id)
      inFlightIdsRef.current.add(entry.id)
      const logId = crypto.randomUUID()
      addEntry({
        id: logId,
        participantId: entry.id,
        firstName: entry.firstName,
        lastName: entry.lastName,
        email: entry.email,
        role: entry.role,
        outcome: 'approved',
        syncStatus: 'syncing',
        scannedAt: Date.now(),
      })

      recordAttendance({ eventId, participantId: entry.id })
        .unwrap()
        .then(() => {
          inFlightIdsRef.current.delete(entry.id)
          if (aliveRef.current) updateStatus(logId, 'synced')
        })
        .catch(() => {
          // The sync never landed — unblock a retry. Keeping the id in
          // `confirmedSyncedIdsRef` would mark every later scan "already attended"
          // without ever calling the API, so the participant could never be checked in.
          inFlightIdsRef.current.delete(entry.id)
          confirmedSyncedIdsRef.current.delete(entry.id)
          if (aliveRef.current) updateStatus(logId, 'error')
        })
    }

    resetTimerRef.current = setTimeout(() => {
      if (!aliveRef.current) return
      setStatus('none')
      setApprovedEntry(null)
    }, STATUS_RESET_MS)
  }

  // // TEMPORARY (frontend-only mock): shows the approved state with fixed mock
  // // data, without recording real attendance. See MOCK_APPROVE_ANY_QR above.
  // function handleApprovedMock() {
  //   if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
  //   setStatus('approved')
  //   setApprovedEntry(MOCK_PARTICIPANT_ENTRY)
  //   playApproved()
  //   resetTimerRef.current = setTimeout(() => {
  //     setStatus('none')
  //     setApprovedEntry(null)
  //   }, STATUS_RESET_MS)
  // }

  function handleRejected() {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    setStatus('rejected')
    setApprovedEntry(null)
    playRejected()
    // Rejected scans never matched a known participant and are never synced to the
    // backend — they're intentionally excluded from the scanned-participants list.
    resetTimerRef.current = setTimeout(() => {
      if (aliveRef.current) setStatus('none')
    }, STATUS_RESET_MS)
  }

  const handleDecode = useCallback(
    async (raw: string) => {
      // if (MOCK_APPROVE_ANY_QR) {
      //   handleApprovedMock()
      //   return
      // }
      const result = await verifyScannerQr(
        raw,
        bundle.event.id,
        scannerMap,
        bundle.org.publicKeyBase64
      )
      if (result.status === 'approved') handleApproved(result.entry)
      else handleRejected()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bundle.event.id, bundle.org.publicKeyBase64, scannerMap]
  )

  const {
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    errorMessage,
    startScanning,
    stopScanning,
  } = useQrScanner({ onDecode: handleDecode })

  // Wait until enumeration has published devices before starting ZXing. Starting on
  // the localStorage camera id during the environment-facing probe overlapped two
  // getUserMedia calls and produced "Could not start video source" on Android
  // tablets (TC-SCAN-007). Cleanup-on-change serializes camera switches.
  useEffect(() => {
    const video = videoRef.current
    if (!video || devices.length === 0 || !selectedDeviceId) return
    startScanning(video)
    return () => {
      stopScanning()
    }
  }, [devices.length, selectedDeviceId, startScanning, stopScanning])

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      stopScanning()
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    }
  }, [stopScanning])

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault()
    setManualError(null)
    setManualSuggestionsOpen(false)
    // Unlike `manualSuggestions` above, the actual check-in match stays email-only and
    // exact — deliberately not widened to name, so submitting never checks in the wrong
    // person off an ambiguous/partial name. Staff searching by name must pick the intended
    // suggestion (which fills the field with that participant's real email via
    // `selectManualSuggestion`) before this succeeds. Same full-width normalization as the
    // suggestions search, for the same reason (#175/#271).
    const needle = formatEmail(manualEmail)
    const found = bundle.participants.find(
      (p) => (p.status === 'ACCEPTED' || p.status === 'ATTENDED') && formatEmail(p.email) === needle
    )
    if (found) {
      setManualEmail('')
      handleApproved(found)
    } else {
      setManualError(t('verify_manual_not_found'))
    }
  }

  function selectManualSuggestion(entry: ScannerParticipantEntry) {
    setManualEmail(entry.email)
    setManualError(null)
    setManualSuggestionsOpen(false)
    manualInputRef.current?.focus()
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {/* Top section: camera + 50/50 result row. On tablet-and-up viewports this
          section is capped to exactly half of the visible viewport height (not
          half on top of everything else) so it never forces the page to scroll
          just to reveal the rest of itself — children shrink to fit via
          `min-h-0` instead of growing past the allotted space. */}
      <div className="flex flex-col gap-4 md:h-[50vh] md:overflow-hidden">
        {/* Camera block */}
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-2 md:min-h-0 md:max-w-none">
          <div className="relative aspect-video max-h-[200px] flex-1 overflow-hidden rounded-2xl bg-[#0D1117] md:aspect-auto md:max-h-none md:min-h-0">
            <video
              ref={videoRef}
              className="h-full w-full object-cover opacity-90"
              autoPlay
              muted
              playsInline
              aria-label="QR code scanner camera feed"
            />
            <ScanCorners />
          </div>

          {/* Camera selector */}
          {devices.length > 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-[#30363D] bg-[#161B22] px-3 py-2">
              <label
                htmlFor="camera-select"
                className="shrink-0 text-xs font-medium text-[#8B949E]"
              >
                {t('verify_camera_label')}
              </label>
              <select
                id="camera-select"
                value={selectedDeviceId ?? ''}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                className="min-w-0 flex-1 cursor-pointer bg-transparent text-xs text-[#E6EDF3] focus:outline-none"
                aria-label={t('verify_camera_label')}
              >
                {devices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId} className="bg-[#161B22]">
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {devices.length === 0 && !errorMessage && (
            <p className="rounded-xl border border-[#30363D] bg-[#161B22] px-3 py-2 text-center text-xs text-[#8B949E]">
              {t('verify_camera_no_devices')}
            </p>
          )}

          {errorMessage && (
            <p className="rounded-xl border border-red-900/50 bg-red-500/10 px-3 py-2 text-center text-xs text-red-400">
              {errorMessage.toLowerCase().includes('permission')
                ? t('verify_camera_permission_denied')
                : errorMessage}
            </p>
          )}
        </div>

        {/* 50/50 result row: approve/reject icon | scanned participant details */}
        <div className="grid flex-1 grid-cols-2 gap-3 md:min-h-0">
          <ResultIcon status={status} />
          <ParticipantInfoTile status={status} approvedEntry={approvedEntry} />
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-[#30363D]" />
        <span className="text-[10px] font-semibold tracking-widest text-[#8B949E] uppercase">
          {t('verify_manual_label')}
        </span>
        <div className="h-px flex-1 bg-[#30363D]" />
      </div>

      {/* Manual email form */}
      <form onSubmit={handleManualSubmit} className="flex flex-col gap-2">
        <div className="relative flex gap-2">
          <input
            ref={manualInputRef}
            type="text"
            inputMode="email"
            autoComplete="off"
            role="combobox"
            aria-expanded={manualSuggestionsOpen && manualSuggestions.length > 0}
            aria-controls="manual-email-suggestions"
            aria-autocomplete="list"
            value={manualEmail}
            onChange={(e) => {
              setManualEmail(e.target.value)
              setManualError(null)
              setManualSuggestionsOpen(true)
            }}
            onFocus={() => setManualSuggestionsOpen(true)}
            onBlur={() => {
              // Defer so a mousedown on a suggestion still registers before the list closes.
              window.setTimeout(() => setManualSuggestionsOpen(false), 150)
            }}
            placeholder={t('verify_manual_placeholder')}
            className="min-w-0 flex-1 rounded-xl border border-[#30363D] bg-[#161B22] px-3 py-2.5 text-sm text-[#E6EDF3] transition placeholder:text-[#8B949E]/50 focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none"
            aria-label={t('verify_manual_placeholder')}
          />
          <Button
            type="submit"
            variant="unstyled"
            disabled={!manualEmail.trim()}
            className="shrink-0 rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-4 py-2.5 text-sm font-semibold text-indigo-300 transition hover:border-indigo-400/60 hover:bg-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-30"
          >
            {t('verify_manual_btn')}
          </Button>
          {manualSuggestionsOpen && manualSuggestions.length > 0 && (
            <ul
              id="manual-email-suggestions"
              role="listbox"
              className="absolute top-full right-0 left-0 z-10 mt-1 max-h-48 overflow-y-auto rounded-xl border border-[#30363D] bg-[#161B22] py-1 shadow-lg"
            >
              {manualSuggestions.map((entry) => (
                <li key={entry.id} role="option">
                  <button
                    type="button"
                    className="flex w-full cursor-pointer flex-col gap-0.5 px-3 py-2 text-left text-sm text-[#E6EDF3] hover:bg-[#21262D]"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectManualSuggestion(entry)}
                  >
                    <span className="font-medium">
                      {formatFullName(entry.firstName, entry.lastName)}
                    </span>
                    <span className="text-xs text-[#8B949E]">{entry.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {manualError && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{manualError}</p>
        )}
      </form>

      {/* Scanned-participants list (session-scoped) */}
      <ScanLogList entries={scanLog} />
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

/**
 * Kiosk-style QR ticket verification page for event staff.
 *
 * Accessible to any Keycloak-authenticated org member with the `eventplanner` feature
 * (or platform admins). Authentication uses the Keycloak JWT — no magic-link token is
 * required in the URL.
 *
 * The `?tk=` tokens only appear inside participant QR codes. They are extracted by
 * `verifyScannerQr` for offline cryptographic verification against the preloaded bundle.
 *
 * Typical flow:
 *   1. Org member navigates to this page from the Event Management page.
 *   2. Page loads the scanner bundle via JWT-authenticated GET /api/v1/events/{id}/scanner-bundle.
 *   3. Staff clicks "Start Scanning" and points camera at participant QR codes.
 *   4. Each scanned URL's `?tk=` token is verified offline against the bundle map.
 *   5. On approval, attendance is recorded via POST /api/v1/events/{id}/participants/{id}/attendance.
 */
export function TicketVerificationPage() {
  const { t } = useTranslation()
  const { id: eventId } = useParams<{ id: string }>()

  const {
    data: rawBundle,
    isLoading,
    isError,
    refetch,
    fulfilledTimeStamp,
  } = useGetScannerBundleAdminQuery(
    { eventId: eventId ?? '' },
    { skip: !eventId, pollingInterval: SCANNER_BUNDLE_POLL_MS }
  )

  const [recordAttendance] = useRecordAttendanceAdminMutation()

  const bundle = rawBundle as ScannerBundle | undefined

  const [phase, setPhase] = useState<Phase>('loading')

  useEffect(() => {
    // Never clobber an in-progress scanning session when the bundle query
    // refetches or its cached object identity changes — staff would otherwise
    // get kicked back to the ready screen mid-check-in.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync phase to query lifecycle
    setPhase((prev) => {
      if (prev === 'scanning') return prev
      if (isLoading) return 'loading'
      if (bundle) return 'ready'
      return prev
    })
  }, [isLoading, bundle])

  const loadError = isError ? t('verify_loading_error') : null

  return (
    /* Fixed dark background — no event branding */
    <div className="min-h-screen" style={{ backgroundColor: '#0D1117', color: '#E6EDF3' }}>
      {/* Subtle dot-grid texture */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, #E6EDF3 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-4xl px-4 py-8">
        {/* Header bar */}
        <div className="mb-8 flex items-center justify-between">
          <Link
            to={`/event-planner/${eventId}`}
            className="flex items-center gap-1.5 rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-xs font-semibold text-indigo-300 transition hover:border-indigo-400/60 hover:bg-indigo-500/20 hover:text-indigo-200"
            aria-label={t('verify_back_to_event')}
          >
            <ArrowLeftIcon />
            {t('verify_back_to_event')}
          </Link>
        </div>

        {/* Phase content */}
        {phase === 'loading' && <LoadingPhase error={loadError} onRetry={() => void refetch()} />}

        {phase === 'ready' && bundle && (
          <ReadyPhase bundle={bundle} onStart={() => setPhase('scanning')} />
        )}

        {phase === 'scanning' && bundle && (
          <ScanningPhase
            bundle={bundle}
            bundleFetchedAt={fulfilledTimeStamp}
            eventId={eventId ?? ''}
            recordAttendance={recordAttendance}
          />
        )}
      </div>
    </div>
  )
}
